import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatUnits } from "ethers";
import ButtonPrimary from "@/components/ButtonPrimary";
import useParticipate from "@/web3Hook/useParticipate";
import useLotteryAmount from "@/web3Hook/useLotteryAmount";
import useGetTokenList from "@/web3Hook/useGetTokenList";
import useGetTokenPoolInfo, { TokenPoolInfo } from "@/web3Hook/useGetTokenPoolInfo";
import useGetTokenSymbol from "@/web3Hook/useGetTokenSymbol";
import useMainNetworkCoin from "@/web3Hook/useMainNetworkCoin";
import { MAINNET_SYMBOL } from "@/collocate";
import useCheckLotteryBuff from "@/web3Hook/useCheckLotteryBuff";

/**
 * 抽奖轮盘组件的属性接口
 * @property {Function} checkLotteryBuff2 - 用于刷新5倍算力加成状态的回调函数
 * @property {boolean} isWinner - 用户是否已拥有5倍算力加成(true=已有,false=没有)
 */
interface LotteryWheelProps {
    checkLotteryBuff2: Function;
    isWinner: boolean;
}

/**
 * LotteryWheel - 抽奖转盘组件
 * 
 * 功能说明:
 * 1. 用户点击按钮参与抽奖,转盘旋转后停止并显示结果
 * 2. 九宫格布局: 5倍算力加成固定在中间位置(index 4), 代币奖励分散在其他8个位置
 * 3. 根据抽奖结果高亮到对应位置并显示中奖内容
 * 4. 如果用户已有5倍算力加成(isWinner=true),则禁止参与抽奖
 * 
 * 抽奖状态码说明:
 * - 0: PENDING (待定/未抽奖)
 * - 1: NO_WIN (未中奖)
 * - 2: BUFF_WIN (中了5倍算力加成)
 * - 3: TOKEN_WIN (中了代币,tokenAddress字段会有具体代币地址)
 */
const LotteryWheel: React.FC<LotteryWheelProps> = ({ checkLotteryBuff2, isWinner }) => {
    const { t } = useTranslation();

    // ===== Hooks 数据获取 =====
    // 检查用户当前的抽奖结果状态
    const { checkLotteryBuff, lotteryStatus, tokenAddress, checkLotteryBuffLod } = useCheckLotteryBuff();
    // 参与抽奖的交易方法和加载状态
    const { participate, participateLod } = useParticipate();
    // 获取抽奖费用(需要消耗的主网币数量)
    const { lotteryAmount, lotteryAmountLod } = useLotteryAmount();
    // 获取可抽奖的代币列表
    const { tokenList } = useGetTokenList();
    // 获取指定代币的奖池信息(奖励数量、概率等)
    const { getTokenPoolInfo } = useGetTokenPoolInfo();
    // 批量获取代币符号(如USDT、USDC等),包含缓存机制
    const { getTokenInfoBatch, tokenInfoCache } = useGetTokenSymbol();
    // 获取用户主网币余额和刷新方法
    const { balance, balanceLod, getMainNetworkCoin } = useMainNetworkCoin();

    // ===== 组件状态管理 =====
    // 是否正在转动动画中
    const [isSpinning, setIsSpinning] = useState(false);
    // 当前高亮的格子索引(-1表示无高亮)
    const [activeIndex, setActiveIndex] = useState(-1);
    // 是否显示抽奖结果弹窗
    const [showResultModal, setShowResultModal] = useState(false);
    // 本次抽奖的结果状态码(0-3)
    const [resultStatus, setResultStatus] = useState<number>(0);
    // 是否显示中奖格子的特殊样式和内容
    const [showWinnerBox, setShowWinnerBox] = useState(false);
    // 代币地址到九宫格位置的映射表 { "0x123...": 0, "0x456...": 1, ... }
    const [tokenPositionMap, setTokenPositionMap] = useState<{ [key: string]: number }>({});
    // 代币地址到奖池详细信息的映射表
    const [tokenInfoMap, setTokenInfoMap] = useState<{ [address: string]: TokenPoolInfo }>({});

    /**
     * Effect 2: 当代币列表获取成功后,并行获取每个代币的详细信息
     * 触发时机: tokenList发生变化(从空数组变为有数据)
     * 作用: 
     * 1. 调用每个代币的getTokenPoolInfo()获取奖池配置(奖励数量、概率等)
     * 2. 调用getTokenInfoBatch()批量获取代币符号(如USDT、DAI等)
     * 这两个操作并行执行以提高性能
     */
    useEffect(() => {
        if (tokenList.length > 0) {
            const fetchTokenInfo = async () => {
                // 并行执行: Promise.all确保两个异步操作同时进行
                await Promise.all([
                    // 获取所有代币的奖池信息
                    Promise.all(tokenList.map(token => getTokenPoolInfo(token))).then(results => {
                        const infoMap: { [address: string]: TokenPoolInfo } = {};
                        tokenList.forEach((token, index) => {
                            const info = results[index];
                            if (info) {
                                // 使用小写地址作为key,统一格式避免大小写问题
                                infoMap[token.toLowerCase()] = info;
                            }
                        });
                        setTokenInfoMap(infoMap);
                    }),
                    // 批量获取所有代币的符号名称
                    getTokenInfoBatch(tokenList)
                ]);
            };
            fetchTokenInfo();
        }
    }, [tokenList, getTokenPoolInfo, getTokenInfoBatch]);

    /**
     * 跑马灯动画的运行顺序
     * 定义格子高亮的顺序,按顺时针围绕中心旋转
     * 九宫格布局如下(数字为index):
     * [0] [1] [2]
     * [3] [4] [5]
     * [6] [7] [8]
     * 
     * 跑马灯路径: 0 -> 1 -> 2 -> 5 -> 8 -> 7 -> 6 -> 3 -> 0(循环)
     * 中间位置4不参与跑马灯,固定为5倍算力加成
     */
    const lightSequence = useMemo(() => [0, 1, 2, 5, 8, 7, 6, 3], []);

    /**
     * Effect 3: 将获取的代币分配到九宫格的不同位置
     * 触发时机: tokenList或lightSequence发生变化
     * 作用: 建立"代币地址 -> 格子位置"的映射关系
     * 例如: { "0xUSDT地址": 0, "0xDAI地址": 1, ... }
     * 最多支持8个代币(lightSequence.length=8),剩余位置标记为"未中奖"
     */
    useEffect(() => {
        if (tokenList.length > 0) {
            const map: { [key: string]: number } = {};
            tokenList.forEach((token, index) => {
                // 只分配前8个代币到跑马灯位置
                if (index < lightSequence.length) {
                    // 使用小写地址作为key,统一格式
                    map[token.toLowerCase()] = lightSequence[index];
                }
            });
            setTokenPositionMap(map);
        }
    }, [tokenList, lightSequence]);

    /**
     * 动态生成九宫格的9个格子数据
     * 依赖: tokenList(代币列表), t(翻译函数), lightSequence(跑马灯顺序)
     * 
     * 生成逻辑:
     * 1. 前8个位置(lightSequence中的位置)根据tokenList分配:
     *    - 如果有对应代币,类型为"token",存储代币地址
     *    - 如果没有对应代币,类型为"nowin"(未中奖占位符)
     * 2. 中间位置(index=4)固定为"buff"类型,代表5倍算力加成
     * 3. 最后按index排序,确保九宫格位置正确
     * 
     * 返回格式: 
     * [
     *   { index: 0, label: "神秘盒", icon: "🎁", type: "token", tokenAddress: "0x..." },
     *   { index: 1, label: "神秘盒", icon: "🎁", type: "token", tokenAddress: "0x..." },
     *   ...
     *   { index: 4, label: "神秘盒", icon: "🎁", type: "buff", tokenAddress: undefined },
     *   ...
     * ]
     */
    const gridItems = useMemo(() => {
        // 生成8个外围格子
        const items = lightSequence.map((position, index) => {
            // 判断当前位置是否有对应的代币
            const hasToken = index < tokenList.length;
            return {
                index: position,                          // 格子在九宫格中的位置(0-8)
                label: t('contract.mysteryBox'),          // 默认显示"神秘盒"
                icon: "🎁",                               // 默认图标
                type: hasToken ? "token" : "nowin",       // token=有代币, nowin=未中奖占位
                tokenAddress: hasToken ? tokenList[index] : undefined,  // 代币地址(如有)
            };
        });

        // 添加中间位置的5倍算力加成格子
        items.push({
            index: 4,                                     // 中间位置
            label: t('contract.mysteryBox'),              // 默认显示"神秘盒"
            icon: "🎁",                                   // 默认图标
            type: "buff",                                 // buff=5倍算力加成
            tokenAddress: undefined,                      // 加成没有代币地址
        });

        // 按index从小到大排序,确保渲染顺序正确
        return items.sort((a, b) => a.index - b.index);
    }, [tokenList, t, lightSequence]);

    /**
     * Effect 4: 根据用户的抽奖结果和5倍加成状态初始化九宫格显示
     * 触发时机: lotteryStatus、tokenAddress、tokenPositionMap、isWinner任一变化
     * 
     * 状态处理逻辑:
     * 1. lotteryStatus === 2 (中了5倍算力加成) 且 isWinner === true:
     *    - 高亮中间格子(index=4)
     *    - showWinnerBox=true 显示特殊样式和"⚡5倍算力"文字
     *    注意: 如果isWinner变为false(用户质押消耗了加成),则重置显示
     * 
     * 2. lotteryStatus === 3 (中了代币):
     *    - 根据tokenAddress在tokenPositionMap中查找对应的格子位置
     *    - 高亮该位置
     *    - showWinnerBox=true 显示特殊样式和代币数量+符号
     *    注意: 代币中奖显示不受isWinner影响,一直保持显示
     * 
     * 3. 其他情况(未抽奖、未中奖、或加成已消耗):
     *    - showWinnerBox=false 隐藏中奖样式
     *    - activeIndex=-1 不高亮任何格子
     * 
     * 注意: 这个Effect只负责初始化显示,不参与抽奖动画过程
     */
    useEffect(() => {
        if (isWinner) {
            // 用户有加成，初始化高亮中间格子
            setShowWinnerBox(true);
            setActiveIndex(4);
        } else {
            // 用户无加成，全部不高亮
            setShowWinnerBox(false);
            setActiveIndex(-1);
        }
    }, [isWinner]);

    // ===== 抽奖交互逻辑 =====

    /**
     * 参与抽奖按钮点击处理函数
     * 
     * 流程:
     * 1. 前置检查:
     *    - 抽奖费用必须大于0
     *    - 用户余额必须充足
     * 
     * 2. 调用participate()发起抽奖交易:
     *    - 第1个参数: 抽奖费用金额
     *    - 第2个参数: 成功/失败提示配置
     *    - 第3个回调: 交易上链成功后执行
     *      * 查询抽奖结果(checkLotteryBuff)
     *      * 刷新余额(getMainNetworkCoin)
     *      * 停止动画并显示结果(stopSpinningAndShowResult)
     *    - 第4个回调: 交易失败后执行
     *      * 停止动画(setIsSpinning(false))
     *    - 第5个回调: 交易发送成功立即执行(还未上链)
     *      * 关闭结果弹窗(setShowResultModal(false))
     *      * 隐藏中奖标记(setShowWinnerBox(false))
     *      * 开始转盘动画(startSpinning)
     */
    const handleParticipate = () => {
        // 检查1: 抽奖费用必须有效
        if (lotteryAmount === BigInt(0)) {
            return;
        }
        // 检查2: 余额是否足够支付抽奖费用
        if (balance < lotteryAmount) {
            return;
        }
        participate(
            lotteryAmount,
            {
                success: '',                              // 成功提示为空(通过弹窗展示)
                error: t('contract.participateFailed'),   // 失败提示文字
            },
            async () => {
                // 回调1: 交易上链成功,查询结果
                const result = await checkLotteryBuff();
                // 刷新用户余额
                getMainNetworkCoin();
                // 停止转盘并显示抽奖结果
                stopSpinningAndShowResult(result.status, result.tokenAddress);
            },
            () => {
                // 回调2: 交易失败,停止动画
                setIsSpinning(false);
            },
            () => {
                // 回调3: 交易发送成功(但还未上链),立即开始动画
                setShowResultModal(false);    // 关闭之前的结果弹窗
                setShowWinnerBox(false);      // 隐藏之前的中奖标记
                startSpinning();              // 开始转盘动画
            }
        );
    };

    // ===== 抽奖动画控制 =====

    /**
     * 开始快速转动动画
     * 
     * 动画说明:
     * - 使用setInterval每100ms切换一次高亮格子
     * - 按照lightSequence顺序循环高亮
     * - 将interval和当前步数存储到window对象,供后续停止动画使用
     * 
     * 状态变化:
     * - isSpinning=true 标记动画进行中
     * - activeIndex=0 从第一个格子开始
     * - window.lotteryInterval 保存定时器ID
     * - window.lotteryCurrentStep 保存当前步数
     */
    const startSpinning = () => {
        setIsSpinning(true);
        setActiveIndex(0);
        let currentStep = 0;
        // 每100ms切换一次高亮格子
        const fastInterval = setInterval(() => {
            setActiveIndex(lightSequence[currentStep % lightSequence.length]);
            currentStep++;
        }, 100);
        // 保存到window对象,方便后续清除
        (window as any).lotteryInterval = fastInterval;
        (window as any).lotteryCurrentStep = currentStep;
    };

    /**
     * 停止快速动画,准备展示最终结果
     * 
     * @param status - 抽奖结果状态码(0-3)
     * @param token - 中奖的代币地址(如果中了代币)
     * 
     * 流程:
     * 1. 清除快速转动的定时器
     * 2. 获取当前动画进行到的步数
     * 3. 调用finalSpinToResult执行减速动画并停在目标位置
     */
    const stopSpinningAndShowResult = (status: number, token: string) => {
        // 清除快速转动定时器
        if ((window as any).lotteryInterval) {
            clearInterval((window as any).lotteryInterval);
        }
        // 获取当前步数,作为减速动画的起点
        const currentStep = (window as any).lotteryCurrentStep || 0;
        // 执行减速动画
        finalSpinToResult(status, token, currentStep);
    };

    /**
     * 执行减速动画并最终停在目标位置
     * 
     * @param status - 抽奖结果状态码: 0=待定, 1=未中奖, 2=5倍加成, 3=代币
     * @param token - 中奖代币地址(仅status=3时有效)
     * @param startStep - 动画开始的步数(继承自快速动画)
     * 
     * 动画分为3个阶段:
     * 
     * 阶段1: 快速旋转(保持100ms速度)
     * - 额外旋转3圈(lightSequence.length * 3步)
     * - 营造转盘快速旋转的视觉效果
     * 
     * 阶段2: 渐进减速
     * - 根据中奖类型执行不同的减速策略:
     * 
     *   情况A - 中了5倍算力(status=2, targetIndex=4):
     *     * 目标: 中间格子
     *     * 策略: 再转3步后直接停在中间
     *     * 速度变化: 每步+100ms(越来越慢)
     * 
     *   情况B - 中了代币(status=3, targetIndex=代币位置):
     *     * 目标: tokenPositionMap中对应的格子位置
     *     * 策略: 逐步减速直到当前位置正好是目标位置
     *     * 速度变化: 每步+80ms
     * 
     *   情况C - 未中奖(status=0或1):
     *     * 目标: 随机选择一个外围格子
     *     * 策略: 逐步减速直到停在随机位置
     *     * 速度变化: 每步+50ms
     * 
     * 阶段3: 停止并展示结果
     * - 设置isSpinning=false停止动画
     * - 延迟300ms后:
     *   * 显示结果弹窗(setShowResultModal(true))
     *   * 如果中奖,显示中奖样式(setShowWinnerBox(true))
     *   * 如果中了5倍加成,调用checkLotteryBuff2()刷新isWinner状态
     */
    const finalSpinToResult = (status: number, token: string, startStep: number) => {
        setResultStatus(status);

        let targetIndex = -1;
        if (status === 2) {
            targetIndex = 4;
        } else if (status === 3 && token) {
            targetIndex = tokenPositionMap[token.toLowerCase()] ?? -1;
        }

        const isWin = status === 2 || status === 3;
        const additionalSteps = lightSequence.length * 3;
        let currentStep = startStep;
        let speed = 100;

        // 递归执行减速动画
        const slowDownAnimation = () => {
            if (currentStep < startStep + additionalSteps) {
                setActiveIndex(lightSequence[currentStep % lightSequence.length]);
                currentStep++;
                setTimeout(slowDownAnimation, speed);
            } else {
                if (isWin && targetIndex >= 0) {
                    if (targetIndex === 4) {
                        const stepsToCenter = 3;
                        if (currentStep < startStep + additionalSteps + stepsToCenter) {
                            setActiveIndex(lightSequence[currentStep % lightSequence.length]);
                            currentStep++;
                            speed += 100;
                            setTimeout(slowDownAnimation, speed);
                        } else {
                            setActiveIndex(4);
                            setIsSpinning(false);
                            setTimeout(() => {
                                setShowResultModal(true);
                                setShowWinnerBox(true);
                                checkLotteryBuff2();
                            }, 300);
                        }
                    } else {
                        const currentPos = lightSequence[currentStep % lightSequence.length];
                        if (currentPos === targetIndex) {
                            setActiveIndex(targetIndex);
                            setIsSpinning(false);
                            setTimeout(() => {
                                setShowResultModal(true);
                                setShowWinnerBox(true);
                            }, 300);
                        } else {
                            setActiveIndex(currentPos);
                            currentStep++;
                            speed += 80;
                            setTimeout(slowDownAnimation, speed);
                        }
                    }
                } else {
                    const targetPos = lightSequence[Math.floor(Math.random() * lightSequence.length)];
                    const currentPos = lightSequence[currentStep % lightSequence.length];
                    if (currentPos === targetPos) {
                        setActiveIndex(targetPos);
                        setIsSpinning(false);
                        setTimeout(() => {
                            setShowResultModal(true);
                        }, 300);
                    } else {
                        setActiveIndex(currentPos);
                        currentStep++;
                        speed += 50;
                        setTimeout(slowDownAnimation, speed);
                    }
                }
            }
        };

        slowDownAnimation();
    };

    // 渲染单个格子
    const renderGridItem = (item: typeof gridItems[0]) => {
        const isStoppedPosition = !isSpinning && activeIndex === item.index;
        const shouldShowResult = showWinnerBox && isStoppedPosition;
        let displayIcon = item.icon;
        let displayLabel = item.label;
        let isActualWin = false;

        if (isWinner && item.index === 4) {
            displayIcon = "⚡";
            displayLabel = '';
            isActualWin = true;
        } else if (shouldShowResult) {
            if (item.type === "buff" && lotteryStatus === 2) {
                displayIcon = "⚡";
                displayLabel = '';
                isActualWin = true;
            } else if (item.type === "token" && lotteryStatus === 3 && item.tokenAddress?.toLowerCase() === tokenAddress.toLowerCase()) {
                displayIcon = "💰";
                displayLabel = '';
                isActualWin = true;
            } else if (lotteryStatus === 1 || item.type === "nowin") {
                displayIcon = "💔";
                displayLabel = t('contract.notWon');
            }
        }

        return (
            <div
                key={item.index}
                className={[
                    "w-[70px] h-[70px] rounded-2xl flex flex-col items-center justify-center",
                    "transition-all duration-300 cursor-default px-2 py-2",
                    isActualWin
                        ? "bg-gradient-to-br from-yellow-400 to-orange-500"
                        : "bg-purple-400/25 shadow-yellow-500/30 shadow-[2px_2px_4px_4px_rgba(6,182,212,0.3)] border border-[rgba(254,240,138,1)]",
                    isSpinning && activeIndex === item.index
                        ? "scale-105 animate-[bounce_0.8s_ease-in-out_infinite]"
                        : "",
                    !isSpinning && activeIndex === item.index && isActualWin
                        ? "ring-2 ring-purple-400 scale-105 shadow-lg border-2 border-purple-400 animate-pulse"
                        : ""
                ].join(" ")}
            >
                <div className="text-3xl mb-1">{displayIcon}</div>
                {displayLabel && (
                    <div className={["text-xs text-center", isActualWin ? "text-red-700" : "text-yellow-200"].join(" ")}>
                        {displayLabel}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <div className="bg-3c p-4 md:p-6 rounded-2xl">
                <div className="flex items-center justify-center mb-8">
                    <div className="grid grid-cols-3 place-items-center gap-x-5 gap-y-4 p-6 rounded-2xl">
                        {gridItems.map(renderGridItem)}
                    </div>
                </div>

                <div className="text-white text-center mb-6">
                    <p className="text-sm md:text-base text-a8 mb-2">
                        {t('contract.lotteryFee')}：
                        {lotteryAmountLod ? (
                            <span className="inline-block w-16 h-4 bg-3a animate-pulse rounded"></span>
                        ) : (
                            `${formatUnits(lotteryAmount, 18)} ${MAINNET_SYMBOL}`
                        )}
                    </p>
                    <p className="text-xs md:text-sm text-a8">
                        {t('contract.clickToParticipate')}
                    </p>
                </div>

                {!isSpinning && !participateLod && !checkLotteryBuffLod && isWinner && (
                    <div className="bg-38 border border-fa text-white text-center py-3 rounded-lg mb-4 text-sm md:text-base">
                        {t('contract.congratulationsReward')}
                    </div>
                )}

                <ButtonPrimary
                    className="h-11.25 md:h-12"
                    text={
                        balance < lotteryAmount && !isSpinning
                            ? '余额不足'
                            : (!isSpinning ? t('contract.participateInLottery') : t('contract.drawing'))
                    }
                    onClick={handleParticipate}
                    loading={participateLod || isSpinning || checkLotteryBuffLod || checkLotteryBuffLod || lotteryAmountLod || balanceLod}
                    disabled={balance < lotteryAmount || isWinner}
                    block
                />
            </div>

            {showResultModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
                    onClick={() => setShowResultModal(false)}>
                    <div className="bg-gradient-to-br from-3c to-2a p-8 rounded-3xl shadow-2xl max-w-md mx-4 transform scale-100 animate-bounce-in"
                        onClick={(e) => e.stopPropagation()}>
                        {resultStatus === 2 || resultStatus === 3 ? (
                            <>
                                <div className="text-center">
                                    <div className="text-6xl mb-4 animate-bounce">🎉</div>
                                    <p className="text-white text-lg mb-6">
                                        {resultStatus === 2 && t('contract.congratulationsFiveTimesBuff')}
                                    </p>
                                    {resultStatus === 3 && tokenAddress && tokenInfoMap[tokenAddress.toLowerCase()] && (
                                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-xl p-4 mb-4">
                                            <p className="text-white text-base mb-2">
                                                {t('contract.rewardDetails')}:
                                            </p>
                                            <p className="text-yellow-300 text-xl font-bold">
                                                💰 {formatUnits(tokenInfoMap[tokenAddress.toLowerCase()].config.rewardAmount, 18)} {tokenInfoCache[tokenAddress.toLowerCase()]?.symbol || 'Token'}
                                            </p>
                                        </div>
                                    )}
                                    <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-500/50 rounded-xl p-4 mb-6">
                                        <p className="text-yellow-300 text-sm">
                                            {t('contract.lotterySuccessTip')}
                                        </p>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-center">
                                    <div className="text-6xl mb-4">💔</div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                        {t('contract.betterLuckNextTime')}
                                    </h2>
                                </div>
                            </>
                        )}
                        <button
                            onClick={() => setShowResultModal(false)}
                            className="w-full bg-gradient-to-r from-fa to-orange-500 hover:from-fa/90 hover:to-orange-500/90 text-white font-semibold py-3 rounded-xl transition-all duration-300 transform hover:scale-105"
                        >
                            {t('contract.close')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LotteryWheel;
